import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  getAdminPeople,
  toggleProfileDisabled,
  toggleResourceRetest,
  cancelUserSubscription,
  getResourceQueries,
  toggleQueryRetestAdmin,
  type AdminPerson,
  type AdminPeopleResponse,
  type AdminResourceQuery,
} from '@/api/adminService';

interface Props {
  apiKey: string;
}

interface ResourceQueriesCache {
  queries: AdminResourceQuery[];
  excludedQueries: string[];
}

export default function AdminPeopleTab({ apiKey }: Props) {
  const [data, setData] = useState<AdminPeopleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());
  const [resourceQueriesCache, setResourceQueriesCache] = useState<Record<string, ResourceQueriesCache>>({});
  const [resourceQueriesLoading, setResourceQueriesLoading] = useState<Set<string>>(new Set());
  const [cancelDialog, setCancelDialog] = useState<{ open: boolean; person: AdminPerson | null }>({
    open: false,
    person: null,
  });
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const limit = 20;

  const fetchPeople = useCallback(async (searchTerm: string, pageNum: number) => {
    setLoading(true);
    try {
      const result = await getAdminPeople(apiKey, searchTerm || undefined, pageNum, limit);
      setData(result);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    fetchPeople(search, page);
  }, [fetchPeople, page]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPeople(value, 1);
    }, 400);
  };

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleResourceExpand = async (resourceId: string) => {
    setExpandedResources((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });

    // Lazy-load queries if not cached
    if (!resourceQueriesCache[resourceId] && !resourceQueriesLoading.has(resourceId)) {
      setResourceQueriesLoading((prev) => new Set(prev).add(resourceId));
      try {
        const result = await getResourceQueries(apiKey, resourceId);
        setResourceQueriesCache((prev) => ({
          ...prev,
          [resourceId]: result,
        }));
      } catch {
        // silently fail
      } finally {
        setResourceQueriesLoading((prev) => {
          const next = new Set(prev);
          next.delete(resourceId);
          return next;
        });
      }
    }
  };

  const handleToggleQueryRetest = async (resourceId: string, query: string, enabled: boolean) => {
    const key = `query-${resourceId}-${query}`;
    addActionLoading(key);

    // Optimistic update
    setResourceQueriesCache((prev) => {
      const cached = prev[resourceId];
      if (!cached) return prev;
      const newExcluded = enabled
        ? cached.excludedQueries.filter((q) => q !== query)
        : [...cached.excludedQueries, query];
      return {
        ...prev,
        [resourceId]: { ...cached, excludedQueries: newExcluded },
      };
    });

    try {
      const result = await toggleQueryRetestAdmin(apiKey, resourceId, query, enabled);
      setResourceQueriesCache((prev) => {
        const cached = prev[resourceId];
        if (!cached) return prev;
        return {
          ...prev,
          [resourceId]: { ...cached, excludedQueries: result.excludedQueries },
        };
      });
    } catch {
      // Revert optimistic update
      setResourceQueriesCache((prev) => {
        const cached = prev[resourceId];
        if (!cached) return prev;
        const revertedExcluded = enabled
          ? [...cached.excludedQueries, query]
          : cached.excludedQueries.filter((q) => q !== query);
        return {
          ...prev,
          [resourceId]: { ...cached, excludedQueries: revertedExcluded },
        };
      });
    } finally {
      removeActionLoading(key);
    }
  };

  const addActionLoading = (id: string) =>
    setActionLoading((prev) => new Set(prev).add(id));
  const removeActionLoading = (id: string) =>
    setActionLoading((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const handleToggleDisable = async (person: AdminPerson) => {
    addActionLoading(`disable-${person.id}`);
    try {
      const result = await toggleProfileDisabled(apiKey, person.id);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          people: prev.people.map((p) =>
            p.id === person.id ? { ...p, isDisabled: result.isDisabled } : p,
          ),
        };
      });
    } catch {
      // silently fail
    } finally {
      removeActionLoading(`disable-${person.id}`);
    }
  };

  const handleToggleRetest = async (resourceId: string) => {
    addActionLoading(`retest-${resourceId}`);
    try {
      const result = await toggleResourceRetest(apiKey, resourceId);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          people: prev.people.map((p) => ({
            ...p,
            resources: p.resources.map((r) =>
              r.id === resourceId ? { ...r, retestEnabled: result.retestEnabled } : r,
            ),
          })),
        };
      });
    } catch {
      // silently fail
    } finally {
      removeActionLoading(`retest-${resourceId}`);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelDialog.person) return;
    const personId = cancelDialog.person.id;
    addActionLoading(`cancel-${personId}`);
    try {
      await cancelUserSubscription(apiKey, personId);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          people: prev.people.map((p) =>
            p.id === personId && p.subscription
              ? { ...p, subscription: { ...p.subscription, cancelAtPeriodEnd: true } }
              : p,
          ),
        };
      });
      setCancelDialog({ open: false, person: null });
    } catch {
      // silently fail
    } finally {
      removeActionLoading(`cancel-${personId}`);
    }
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  const getSubBadge = (person: AdminPerson) => {
    if (!person.subscription) return null;
    const { status, cancelAtPeriodEnd } = person.subscription;
    if (cancelAtPeriodEnd) {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Canceling</Badge>;
    }
    if (status === 'active') {
      return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
    }
    if (status === 'trialing') {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Trialing</Badge>;
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search by email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="max-w-sm"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchPeople(search, page)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        {data && (
          <span className="text-sm text-muted-foreground ml-auto">
            {data.total} user{data.total !== 1 ? 's' : ''} total
          </span>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-3 pr-2 w-8"></th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Plan</th>
                  <th className="p-3 text-right">Resources</th>
                  <th className="p-3 text-right">Retest Active</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!data || data.people.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-muted-foreground">
                      {loading ? 'Loading...' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  data.people.map((person) => {
                    const isExpanded = expandedRows.has(person.id);
                    const retestCount = person.resources.filter((r) => r.retestEnabled).length;
                    return (
                      <PersonRow
                        key={person.id}
                        person={person}
                        isExpanded={isExpanded}
                        retestCount={retestCount}
                        subBadge={getSubBadge(person)}
                        actionLoading={actionLoading}
                        expandedResources={expandedResources}
                        resourceQueriesCache={resourceQueriesCache}
                        resourceQueriesLoading={resourceQueriesLoading}
                        onToggleExpand={() => toggleExpand(person.id)}
                        onToggleDisable={() => handleToggleDisable(person)}
                        onToggleRetest={handleToggleRetest}
                        onToggleResourceExpand={toggleResourceExpand}
                        onToggleQueryRetest={handleToggleQueryRetest}
                        onCancelSub={() => setCancelDialog({ open: true, person })}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <Dialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ open, person: open ? cancelDialog.person : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the subscription for{' '}
              <strong>{cancelDialog.person?.email}</strong>? This will set it to cancel at the end of the current billing period.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setCancelDialog({ open: false, person: null })}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={actionLoading.has(`cancel-${cancelDialog.person?.id}`)}
            >
              {actionLoading.has(`cancel-${cancelDialog.person?.id}`) ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PersonRow({
  person,
  isExpanded,
  retestCount,
  subBadge,
  actionLoading,
  expandedResources,
  resourceQueriesCache,
  resourceQueriesLoading,
  onToggleExpand,
  onToggleDisable,
  onToggleRetest,
  onToggleResourceExpand,
  onToggleQueryRetest,
  onCancelSub,
}: {
  person: AdminPerson;
  isExpanded: boolean;
  retestCount: number;
  subBadge: React.ReactNode;
  actionLoading: Set<string>;
  expandedResources: Set<string>;
  resourceQueriesCache: Record<string, { queries: AdminResourceQuery[]; excludedQueries: string[] }>;
  resourceQueriesLoading: Set<string>;
  onToggleExpand: () => void;
  onToggleDisable: () => void;
  onToggleRetest: (resourceId: string) => void;
  onToggleResourceExpand: (resourceId: string) => void;
  onToggleQueryRetest: (resourceId: string, query: string, enabled: boolean) => void;
  onCancelSub: () => void;
}) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ') || '-';
  const canCancelSub = person.subscription &&
    ['active', 'trialing'].includes(person.subscription.status) &&
    !person.subscription.cancelAtPeriodEnd;

  return (
    <>
      <tr className="border-b last:border-0 hover:bg-muted/50">
        <td className="p-3 pr-2">
          {person.resources.length > 0 && (
            <button
              onClick={onToggleExpand}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg
                className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </td>
        <td className="p-3 font-mono text-xs">{person.email}</td>
        <td className="p-3">{name}</td>
        <td className="p-3">
          <div className="flex items-center gap-1.5">
            {person.isDisabled && (
              <Badge className="bg-red-100 text-red-800 border-red-200">Disabled</Badge>
            )}
            {subBadge}
            {!person.subscription && !person.isDisabled && (
              <span className="text-muted-foreground text-xs">Free</span>
            )}
          </div>
        </td>
        <td className="p-3 text-xs">{person.subscription?.plan ?? '-'}</td>
        <td className="p-3 text-right">{person.resources.length}</td>
        <td className="p-3 text-right">{retestCount}</td>
        <td className="p-3 text-right">
          <div className="flex items-center justify-end gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={onToggleDisable}
              disabled={actionLoading.has(`disable-${person.id}`)}
            >
              {person.isDisabled ? 'Enable' : 'Disable'}
            </Button>
            {canCancelSub && (
              <Button
                variant="destructive"
                size="sm"
                className="text-xs h-7 px-2"
                onClick={onCancelSub}
              >
                Cancel Sub
              </Button>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && person.resources.length > 0 && (
        <tr className="bg-muted/30">
          <td colSpan={8} className="p-0">
            <div className="px-6 py-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-2 pr-2 w-6"></th>
                    <th className="pb-2 pr-4">URL</th>
                    <th className="pb-2 pr-4">Domain</th>
                    <th className="pb-2 pr-4 text-center">llms.txt</th>
                    <th className="pb-2 pr-4">Last Retested</th>
                    <th className="pb-2 pr-4 text-center">Excluded</th>
                    <th className="pb-2 text-right">Retest</th>
                  </tr>
                </thead>
                <tbody>
                  {person.resources.map((resource) => {
                    const isResourceExpanded = expandedResources.has(resource.id);
                    const cachedData = resourceQueriesCache[resource.id];
                    const isLoadingQueries = resourceQueriesLoading.has(resource.id);
                    const excludedCount = resource.retestExcludedQueries?.length ?? 0;

                    return (
                      <ResourceRow
                        key={resource.id}
                        resource={resource}
                        isExpanded={isResourceExpanded}
                        excludedCount={excludedCount}
                        cachedData={cachedData}
                        isLoadingQueries={isLoadingQueries}
                        actionLoading={actionLoading}
                        onToggleExpand={() => onToggleResourceExpand(resource.id)}
                        onToggleRetest={() => onToggleRetest(resource.id)}
                        onToggleQueryRetest={onToggleQueryRetest}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ResourceRow({
  resource,
  isExpanded,
  excludedCount,
  cachedData,
  isLoadingQueries,
  actionLoading,
  onToggleExpand,
  onToggleRetest,
  onToggleQueryRetest,
}: {
  resource: { id: string; url: string; domain: string | null; retestEnabled: boolean; lastReTestedAt: string | null; hasLlmsTxt: boolean };
  isExpanded: boolean;
  excludedCount: number;
  cachedData: { queries: AdminResourceQuery[]; excludedQueries: string[] } | undefined;
  isLoadingQueries: boolean;
  actionLoading: Set<string>;
  onToggleExpand: () => void;
  onToggleRetest: () => void;
  onToggleQueryRetest: (resourceId: string, query: string, enabled: boolean) => void;
}) {
  return (
    <>
      <tr className="border-t border-muted">
        <td className="py-2 pr-2">
          <button
            onClick={onToggleExpand}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg
              className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </td>
        <td className="py-2 pr-4 font-mono max-w-[300px] truncate">
          {resource.url}
        </td>
        <td className="py-2 pr-4">{resource.domain ?? '-'}</td>
        <td className="py-2 pr-4 text-center">
          {resource.hasLlmsTxt ? (
            <span className="text-green-600">Yes</span>
          ) : (
            <span className="text-muted-foreground">No</span>
          )}
        </td>
        <td className="py-2 pr-4">
          {resource.lastReTestedAt
            ? new Date(resource.lastReTestedAt).toLocaleDateString()
            : '-'}
        </td>
        <td className="py-2 pr-4 text-center">
          {excludedCount > 0 ? (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0">
              {excludedCount} excluded
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>
        <td className="py-2 text-right">
          <Switch
            checked={resource.retestEnabled}
            onCheckedChange={onToggleRetest}
            disabled={actionLoading.has(`retest-${resource.id}`)}
          />
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="p-0">
            <div className="pl-8 pr-4 py-2 bg-muted/20">
              {isLoadingQueries ? (
                <div className="py-3 text-center text-muted-foreground text-xs">
                  Loading queries...
                </div>
              ) : cachedData && cachedData.queries.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="pb-1.5 pr-4">Query</th>
                      <th className="pb-1.5 pr-4">Last Tested</th>
                      <th className="pb-1.5 pr-4 text-center">Mentions Site</th>
                      <th className="pb-1.5 text-right">Retest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cachedData.queries.map((q) => {
                      const isExcluded = cachedData.excludedQueries.includes(q.query);
                      const loadingKey = `query-${resource.id}-${q.query}`;
                      return (
                        <tr key={q.query} className="border-t border-muted/50">
                          <td className="py-1.5 pr-4 max-w-[250px] truncate" title={q.query}>
                            {q.query}
                          </td>
                          <td className="py-1.5 pr-4">
                            {q.lastTestedAt
                              ? new Date(q.lastTestedAt).toLocaleDateString()
                              : '-'}
                          </td>
                          <td className="py-1.5 pr-4 text-center">
                            {q.mentionsWebsite ? (
                              <span className="text-green-600">Yes</span>
                            ) : (
                              <span className="text-muted-foreground">No</span>
                            )}
                          </td>
                          <td className="py-1.5 text-right">
                            <Switch
                              checked={!isExcluded}
                              onCheckedChange={(checked) =>
                                onToggleQueryRetest(resource.id, q.query, checked)
                              }
                              disabled={actionLoading.has(loadingKey)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : cachedData ? (
                <div className="py-3 text-center text-muted-foreground text-xs">
                  No queries found for this resource
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
